'use client'

import Link from 'next/link'
import {
  fulfillmentRequestGetRequest,
  parseFulfillmentRequestFailureKind,
  type FulfillmentRequestFailureKind,
} from '@entities/fulfillment-request'
import { FulfillmentRequestWizard, requestWizardEditPath } from '@features/fulfillment-request-wizard'
import { useQuery } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { Card, CardContent } from '@ui/base/card'
import { typeClass } from '@utils/typography'

type Props = { mode: 'create' } | { mode: 'edit'; requestId: string }

const readFailureContent: Record<
  FulfillmentRequestFailureKind,
  { title: string; description: string; variant: 'destructive' | 'warning' }
> = {
  validation: {
    title: 'Не удалось прочитать заявку',
    description: 'Сервер не смог обработать адрес заявки. Откройте её заново из списка.',
    variant: 'destructive',
  },
  unauthorized: {
    title: 'Войдите, чтобы открыть заявку',
    description: 'После входа вы вернётесь к этой же заявке.',
    variant: 'warning',
  },
  forbidden: {
    title: 'Заявка принадлежит другому пользователю',
    description: 'Исправлять заявку может только тот, кто её создал.',
    variant: 'warning',
  },
  'not-found': {
    title: 'Заявка не найдена',
    description: 'Возможно, её удалили или адрес устарел.',
    variant: 'warning',
  },
  conflict: {
    title: 'Заявка изменилась',
    description: 'Откройте её заново из списка своих заявок.',
    variant: 'warning',
  },
  'too-many-requests': {
    title: 'Слишком много запросов',
    description: 'Повторите попытку позже.',
    variant: 'warning',
  },
  unknown: {
    title: 'Не удалось загрузить заявку',
    description: 'Проверьте соединение и повторите попытку.',
    variant: 'destructive',
  },
}

const PageHeader = ({ mode }: { mode: Props['mode'] }) => (
  <header className="mb-6 grid gap-2">
    <h1 className={typeClass.pageTitle}>{mode === 'create' ? 'Заявка на просчёт' : 'Исправить заявку'}</h1>
    <p className={typeClass.body}>
      {mode === 'create'
        ? 'Опишите задачу один раз. Заявку увидят фулфилменты, которые работают по нужной схеме, возят на ваши площадки и подходят по географии, — каждый ответит своей ценой.'
        : 'Исправьте замечания поддержки и отправьте заявку на повторную проверку.'}
    </p>
    <Button variant="link" size="sm" className="justify-start" render={<Link href="/fulfillment/requests" />}>
      Мои заявки
    </Button>
  </header>
)

const ReadFailure = ({ requestId, error, retry }: { requestId: string; error: unknown; retry: () => void }) => {
  const kind = parseFulfillmentRequestFailureKind(error)
  const content = readFailureContent[kind]

  return (
    <Alert variant={content.variant} role="alert">
      <AlertTitle>{content.title}</AlertTitle>
      <AlertDescription>
        <p>{content.description}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          {kind === 'unauthorized' ? (
            <Button
              variant="outline"
              render={<Link href={`/auth/login?callbackUrl=${encodeURIComponent(requestWizardEditPath(requestId))}`} />}
            >
              Войти
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={retry}>
              Повторить
            </Button>
          )}
          <Button variant="ghost" render={<Link href="/fulfillment/requests" />}>
            К моим заявкам
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}

const EditRequestForm = ({ requestId }: { requestId: string }) => {
  const requestQuery = useQuery(fulfillmentRequestGetRequest(requestId))

  if (requestQuery.isPending) {
    return (
      <Card role="status" aria-live="polite" aria-busy="true">
        <CardContent>
          <p className={typeClass.body}>Загружаем заявку…</p>
        </CardContent>
      </Card>
    )
  }

  if (requestQuery.isError) {
    return <ReadFailure requestId={requestId} error={requestQuery.error} retry={() => void requestQuery.refetch()} />
  }

  const snapshot = requestQuery.data
  if (snapshot.status !== 'rejected') {
    return (
      <Alert variant="info" role="status">
        <AlertTitle>Эту заявку сейчас нельзя исправить</AlertTitle>
        <AlertDescription>
          Правка доступна только для заявки, которую поддержка вернула на доработку. Текущее состояние заявки уже
          изменилось — откройте список и посмотрите его пояснение.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="grid gap-5">
      <Alert variant="warning">
        <AlertTitle>Что нужно исправить</AlertTitle>
        <AlertDescription>{snapshot.decline_reason || 'Поддержка вернула заявку без пояснения.'}</AlertDescription>
      </Alert>
      <FulfillmentRequestWizard key={`${snapshot.id}:${snapshot.revision}`} mode="edit" snapshot={snapshot} />
    </div>
  )
}

/** Страница собирает общий мастер в режиме создания либо загружает снимок для безопасной правки. */
export const FulfillmentRequestFormPage = (props: Props) => (
  <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
    <PageHeader mode={props.mode} />
    {props.mode === 'create' ? (
      <FulfillmentRequestWizard mode="create" />
    ) : (
      <EditRequestForm requestId={props.requestId} />
    )}
  </main>
)
